// /reportes/:id — the closed-report reading view for desktop (design
// handoff reporte_escritorio §2). Replaces opening the public /r/:token
// link or the PWA's /registros/:id, both built for phones and for the
// field. Lives OUTSIDE the Shell (no sidebar; opens in its own tab from
// DetalleTienda's «Ir al reporte ↗»), so it renders its own sticky
// .rp-top chrome and fires the facility-dashboard query itself.
//
// Everything on screen is frozen at close time (reports.signedAt /
// closedAt gates every mutating endpoint — CLAUDE.md §5, ADR-0098) —
// except «Estado hoy», the live delegation / observation state of each
// finding, which is deliberately labelled as live so it is never read
// as part of the acta. The observations themselves (text, severity,
// area · item, photos) come from GET /reports/:id's frozen bundle —
// core-api resolves the findings snapshot and cuts evidence at the
// terminal instant there — never from the live per-asset feed, so a
// photo attached or a description edited after closure cannot leak into
// a document whose seal says its content no longer changes.
//
// Scope decisions taken with the handoff (do not re-open here): no
// «Descargar PDF», no numeric per-section score (only «x/y cumplen» +
// the compliance bar, matching the PWA's FacilityActaCerrada), no
// visit-duration meta, no signatures panel.

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { isReportTerminal, reportTerminalAt } from "@bitacorapro/core-engine";
import type {
  AssetObservation,
  BlockItemState,
  BlockState,
  FacilityDashboardEntry,
  ReportDetailResponse,
} from "@bitacorapro/api-types";
import { isSkeletonVisible } from "@bitacorapro/shared/loading";
import { EvidenciaCell } from "../components/EvidenciaCell";
import { PageError } from "../components/PageError";
import { PageSkeleton } from "../components/PageSkeleton";
import { ApiError } from "../lib/api/error";
import { backOriginState } from "../lib/backOrigin";
import {
  useAssetObservations,
  useFacilityDashboard,
  useRecipe,
  useReportBlockItems,
  useReportBlocks,
  useReportDetail,
} from "../lib/api/queries";
import { isFacilityDashboardEnabled } from "../lib/capabilities";
import { useQueryView } from "../lib/loading/useQueryView";
import { useSkeletonPhase } from "../lib/loading/useSkeletonPhase";
import { entryEstadoPill } from "../lib/postventa/entryThread";
import {
  fmtDate,
  observationHeadline,
  observationStatusLabel,
  observationStatusPillClass,
  sevLabel,
  sevPillClass,
  storeSubtitle,
  storeTitle,
} from "../lib/util";
import {
  RpBodySkeleton,
  RpDetSkeleton,
  RpFactsSkeleton,
  RpHeaderSkeleton,
  RpItemsSkeleton,
  RpSecsSkeleton,
} from "./ReporteSkeleton";

// Derived from the wire schema (blockItemStateSchema.shape.supervisorVerdict,
// through its inferred type) rather than re-listed by hand, so a verdict
// the engine adds fails typecheck here instead of silently rendering as
// «Sin revisar» inside a legal document.
type Verdict = BlockItemState["supervisorVerdict"];

// Supervisor wins, the AI proposal fills the rest — same rule as the
// PWA's FacilityActaCerrada.effectiveVerdict so both renders of one
// acta agree on every pill.
function effectiveVerdict(item: BlockItemState): Verdict {
  return item.supervisorVerdict ?? item.aiProposedVerdict;
}

function needsAttention(v: Verdict): boolean {
  return v === "no_cumple" || v === "con_obs";
}

type AreaCounts = { total: number; cumple: number; na: number; attention: number };

// «x/y cumplen» counts every item in the bloque (n_a included in y),
// matching FacilityActaCerrada.blockCountsFor — the PWA and this view
// must print the same fraction for the same acta.
function countsFor(items: readonly BlockItemState[]): AreaCounts {
  let cumple = 0;
  let na = 0;
  let attention = 0;
  for (const it of items) {
    const v = effectiveVerdict(it);
    if (v === "cumple") cumple += 1;
    else if (v === "n_a") na += 1;
    else if (needsAttention(v)) attention += 1;
  }
  return { total: items.length, cumple, na, attention };
}

// The compliance bar's denominator is the applicable items only: a
// section whose every item is «No aplica» has nothing to comply with,
// so it must not paint as 0 % non-compliance — and an empty section
// must not paint as 100 %. Null = nothing applicable (track only).
function compliancePct(c: AreaCounts): number | null {
  const applicable = c.total - c.na;
  return applicable > 0 ? Math.round((c.cumple / applicable) * 100) : null;
}

// byAssetVisits is capped server-side (VISITS_PER_ASSET in core-api's
// facility-dashboard route). Mirrored here only to decide when «Primera
// visita registrada» is provable: a report sitting at the cap's last
// slot may well have an older predecessor the payload dropped.
const VISITS_PER_ASSET_CAP = 6;

type FrozenFinding = ReportDetailResponse["findings"][number];
type FrozenEvidence = ReportDetailResponse["evidence"][number];

// One row of «Observaciones levantadas», built from the frozen bundle.
// Optional wire fields are normalised to `null` because
// observationHeadline's input type asks for `| null`.
type FrozenObservation = {
  id: string;
  areaName: string;
  element: string | null;
  aiTitle: string | null;
  humanDescription: string | null;
  aiDescription: string | null;
  severity: string;
  photoUrls: string[];
};

function freezeObservations(
  findings: readonly FrozenFinding[],
  evidence: readonly FrozenEvidence[]
): FrozenObservation[] {
  const photosByFinding = new Map<string, string[]>();
  for (const e of evidence) {
    if (e.evidenceType !== "photo") continue;
    const list = photosByFinding.get(e.findingId);
    if (list) list.push(e.url);
    else photosByFinding.set(e.findingId, [e.url]);
  }
  return findings.map((f) => ({
    id: f.id,
    areaName: f.areaName,
    element: f.element ?? null,
    aiTitle: f.aiTitle ?? null,
    humanDescription: f.humanDescription ?? null,
    aiDescription: f.aiDescription ?? null,
    severity: f.severity,
    photoUrls: photosByFinding.get(f.id) ?? [],
  }));
}

// «Estado hoy» pill. `title` carries the reason when the state is
// unknown, so «Estado no disponible» is never confused with a genuine
// «Sin asignar» — the dashboard aggregate is a recency-capped org-wide
// window, and an old acta's findings simply fall outside it.
type Estado = { cls: string; label: string; title?: string };

const ESTADO_PENDING: Estado = { cls: "st-pill--idle", label: "…" };

function estadoUnavailable(title: string): Estado {
  return { cls: "st-pill--idle", label: "Estado no disponible", title };
}

// "16 de mayo de 2026"
function fmtLongDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("es-CL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
}

// "11:42"
function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("es-CL", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

// "16-may-2026" — the compact form the seal, the crumb and the tab
// title share. Built from parts so the trailing "." some ICU builds put
// on an abbreviated Spanish month never leaks into the middle of it.
function fmtShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const parts = new Intl.DateTimeFormat("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).formatToParts(d);
  const get = (t: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === t)?.value.replace(".", "") ?? "";
  return `${get("day")}-${get("month")}-${get("year")}`;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

// Never distinguish "not found" from "out of scope": a 403 on a report
// id the caller cannot see reads the same as a 404 (tenant boundary,
// CLAUDE.md §5).
function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 403);
}

// Join key for the checklist-finding ↔ item match. The materializer
// writes findings.areaName = inspection_areas.label and findings.element
// = the item label (core-engine checklist-findings.ts), so this is the
// real join, not a heuristic — normalised anyway so a trailing space or
// a case edit on the template never silently drops a link.
function matchKey(area: string | null | undefined, element: string | null | undefined): string {
  return `${(area ?? "").trim().toLowerCase()}\u0000${(element ?? "").trim().toLowerCase()}`;
}

function CheckIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="m5 12 5 5L20 7" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    </svg>
  );
}

function VerdictPill({ verdict }: { verdict: Verdict }) {
  if (verdict === "cumple") return <span className="st-pill st-pill--ok">Cumple</span>;
  if (verdict === "no_cumple") return <span className="st-pill st-pill--crit">No cumple</span>;
  if (verdict === "con_obs") {
    return (
      <span className="st-pill st-pill--warn">
        <AlertIcon /> Con observación
      </span>
    );
  }
  if (verdict === "n_a") return <span className="st-pill st-pill--idle">No aplica</span>;
  return <span className="st-pill st-pill--idle">Sin revisar</span>;
}

// Inline failure for one panel: the rest of the acta stays rendered
// (it is already loaded and frozen in cache), only this panel offers
// a retry.
function PanelError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rp-panel__error" role="alert">
      <span>{message}</span>
      <button type="button" className="btn btn--ghost btn--sm" onClick={onRetry}>
        Reintentar
      </button>
    </div>
  );
}

// Sticky chrome. `storeName`/`assetId` are optional so the loading and
// error states can render the same bar with only the «Tiendas» crumb.
function RpTop({
  assetId,
  storeName,
  fecha,
}: {
  assetId?: string | undefined;
  storeName?: string | undefined;
  fecha?: string | undefined;
}) {
  return (
    <header className="rp-top">
      <div className="rp-top__brand">
        <span className="sidebar__brand-name">BitácoraPro</span>
      </div>
      <nav className="topbar__crumbs" aria-label="Ruta">
        <span className="crumb-group">
          <Link className="crumb" to="/tiendas">
            Tiendas
          </Link>
        </span>
        {assetId && storeName && (
          <span className="crumb-group">
            <span className="crumb-sep">/</span>
            <Link className="crumb" to={`/tiendas/${assetId}`}>
              {storeName}
            </Link>
          </span>
        )}
        {fecha && (
          <span className="crumb-group">
            <span className="crumb-sep">/</span>
            <span className="crumb crumb--current" aria-current="page">
              Reporte {fecha}
            </span>
          </span>
        )}
      </nav>
    </header>
  );
}

export function Reporte() {
  const { id } = useParams<{ id: string }>();
  const reportQ = useReportDetail(id);
  // The page's loading contract (ADR-0166): only the detail row gates the
  // page; every other read degrades inside its own panel.
  const reportView = useQueryView(reportQ);
  const reportRow = reportQ.data?.report;
  // Normalised to `?? null`: the wire row's optional fields infer as
  // `string | undefined`, which exactOptionalPropertyTypes refuses to
  // hand to the engine's `?: string | Date | null` parameters — and
  // string-typed inputs pin reportTerminalAt's generic to `string`.
  const terminalInput = reportRow
    ? {
        status: reportRow.status,
        closedAt: reportRow.closedAt ?? null,
        signedAt: reportRow.signedAt ?? null,
      }
    : null;
  const terminal = terminalInput ? isReportTerminal(terminalInput) : false;
  const terminalAt = terminalInput ? reportTerminalAt(terminalInput) : null;

  // The frozen reads only fire once the report is known to be terminal
  // — an in-progress visit renders the «en curso» block and nothing
  // else, so its bloques never land in the Infinity-staleTime cache.
  const blocksQ = useReportBlocks(id, terminal);
  // Disabled until the detail row proves the report terminal: waiting on
  // it keeps the sections in their skeleton instead of «disabled».
  const blocksView = useQueryView(blocksQ, { waitingOn: [reportQ] });
  const blocks = useMemo(
    () => (blocksQ.data?.blocks ?? []).slice().sort((a, b) => a.displayOrder - b.displayOrder),
    [blocksQ.data]
  );
  const areaIds = useMemo(() => blocks.map((b) => b.inspectionAreaId), [blocks]);
  const items = useReportBlockItems(id, areaIds);

  const assetId = reportRow?.assetId;

  // Outside the Shell, so the dashboard aggregate (store identity, zone,
  // the per-asset visits list for the delta, the delegation state per
  // finding) must be fired here. Gated on the recipe exactly like
  // Shell.tsx — `enabled` is per-observer.
  const recipeQ = useRecipe();
  const recipe = recipeQ.data;
  const facilityEnabled = isFacilityDashboardEnabled(recipe);
  const dashQ = useFacilityDashboard(facilityEnabled);
  const dash = dashQ.data;
  // A disabled query is not "loading" in TanStack terms, so `isLoading`
  // alone would let the delta card flash a verdict before the recipe
  // (and then the aggregate) resolve. Pending = the recipe is unknown,
  // or the aggregate is enabled and not here yet. A failed aggregate is
  // not pending: the screen degrades (store fallbacks, «—» delta) and
  // never blocks the acta on it — and neither is a failed recipe read,
  // which would otherwise leave «…» on screen forever.
  const dashPending =
    (recipe === undefined && !recipeQ.error) || (facilityEnabled && !dash && !dashQ.error);

  // «Estado hoy» source is recipe-driven, never a vertical-slug branch.
  // The facility dashboard aggregate already carries each finding's
  // observation thread (ADR-0134: state, engine phase, closure overlay),
  // so a recipe with that dashboard reads it from there. Any other recipe
  // that declares an asset-scoped observation lifecycle
  // (observationStatuses, broker_postventa) reads the live per-asset
  // feed. The live feed is the only reason to fetch it, so it is enabled
  // only in that case.
  const observationStatuses = recipe?.observationStatuses ?? [];
  const liveLifecycle = observationStatuses.length > 0 && !facilityEnabled;
  const obsQ = useAssetObservations(terminal && liveLifecycle ? assetId : undefined);
  const liveById = useMemo(() => {
    const m = new Map<string, AssetObservation>();
    for (const o of obsQ.data?.observations ?? []) m.set(o.id, o);
    return m;
  }, [obsQ.data]);

  const tienda = useMemo(
    () => (dash && assetId ? (dash.byAsset.find((a) => a.id === assetId) ?? null) : null),
    [dash, assetId]
  );
  const zoneName = useMemo(() => {
    if (!dash || !tienda?.assetGroupId) return null;
    return dash.byAssetGroup.find((g) => g.id === tienda.assetGroupId)?.name ?? null;
  }, [dash, tienda]);
  // The report row snapshots address/unit at creation, so the header
  // can paint before (or without) the dashboard aggregate.
  const storeName = tienda ? storeTitle(tienda) : (reportRow?.unit ?? reportRow?.address ?? "—");
  // «código» in the handoff eyebrow: the dashboard has no store-code
  // field, so this is the same secondary identity DetalleTienda's
  // eyebrow uses (the address, when the unit is the title).
  const storeCode = tienda ? storeSubtitle(tienda) : null;
  const brandName = tienda?.brandName ?? reportQ.data?.brand?.name ?? null;

  const visits = useMemo(
    () => (dash && assetId ? (dash.byAssetVisits[assetId] ?? []) : []),
    [dash, assetId]
  );
  // Live thread state per finding, for «Estado hoy» on facility orgs.
  const entryById = useMemo(() => {
    const m = new Map<string, FacilityDashboardEntry>();
    for (const e of dash?.entries ?? []) m.set(e.id, e);
    return m;
  }, [dash]);

  // The frozen observations of this acta: identity, text, severity and
  // photos all from the detail bundle (see the file header).
  const observations = useMemo(
    () => freezeObservations(reportQ.data?.findings ?? [], reportQ.data?.evidence ?? []),
    [reportQ.data]
  );
  const obsByAreaItem = useMemo(() => {
    const m = new Map<string, FrozenObservation[]>();
    for (const o of observations) {
      const k = matchKey(o.areaName, o.element);
      const list = m.get(k);
      if (list) list.push(o);
      else m.set(k, [o]);
    }
    return m;
  }, [observations]);

  // <details> open state, owned by React so «Expandir todo / Contraer
  // todo» can derive its label from the real state. Seeded per area the
  // first time its items arrive: open when anything needs attention.
  const [openByArea, setOpenByArea] = useState<Record<string, boolean>>({});
  useEffect(() => {
    setOpenByArea((prev) => {
      let next = prev;
      for (const [areaId, data] of Object.entries(items.byArea)) {
        if (areaId in prev) continue;
        if (next === prev) next = { ...prev };
        next[areaId] = data.items.some((it) => needsAttention(effectiveVerdict(it)));
      }
      return next;
    });
  }, [items.byArea]);
  const allOpen = blocks.length > 0 && blocks.every((b) => openByArea[b.inspectionAreaId] === true);
  function setOpen(areaId: string, open: boolean) {
    setOpenByArea((prev) => (prev[areaId] === open ? prev : { ...prev, [areaId]: open }));
  }
  function toggleAll() {
    const target = !allOpen;
    setOpenByArea((prev) => {
      const next = { ...prev };
      for (const b of blocks) next[b.inspectionAreaId] = target;
      return next;
    });
  }

  const shortDate = terminalAt ? fmtShortDate(terminalAt) : undefined;
  useEffect(() => {
    if (!reportRow || !terminal || !shortDate) return;
    const previous = document.title;
    document.title = `Reporte ${shortDate} · ${storeName} — BitácoraPro`;
    return () => {
      document.title = previous;
    };
  }, [reportRow, terminal, shortDate, storeName]);

  // --- Error / loading / non-terminal gates ------------------------------

  // Only the detail row is fatal: the bloques, the live feed and the
  // dashboard aggregate are accessory and each degrades inside its own
  // panel. A 404 from an accessory read must never relabel a report that
  // is loaded and visible as «No encontramos este reporte».

  // First load: the real chrome and a header frame land at once; only the
  // acta body skeletonizes, on the real panels (ReporteSkeleton.tsx).
  if (reportView.kind === "loading") {
    return (
      <>
        <RpTop />
        <main className="rp">
          <RpHeaderSkeleton />
          <PageSkeleton label="Cargando el reporte…" visible={reportView.visible}>
            <RpBodySkeleton />
          </PageSkeleton>
          {reportView.slow && (
            <div className="notice-banner notice-banner--action">
              <span>
                <strong>Esto está tardando más de lo normal.</strong> Revisa tu conexión. Seguimos
                intentando.
              </span>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => void reportQ.refetch()}
              >
                Reintentar
              </button>
            </div>
          )}
        </main>
      </>
    );
  }

  // `disabled` = no id in the URL: nothing is on its way, so it reads as
  // not found rather than a skeleton forever.
  if (
    reportView.kind === "empty" ||
    reportView.kind === "disabled" ||
    (reportView.kind === "error" && isNotFound(reportView.error))
  ) {
    return (
      <>
        <RpTop />
        <main className="rp">
          <div className="empty">
            <div className="empty__title">No encontramos este reporte</div>
            <div className="empty__sub">Puede que ya no exista o que no tengas acceso a él.</div>
            <div className="rp-empty__actions">
              <Link to="/tiendas" className="btn btn--secondary btn--sm">
                Volver a mis tiendas
              </Link>
            </div>
          </div>
        </main>
      </>
    );
  }

  if (reportView.kind === "error") {
    return (
      <>
        <RpTop />
        <main className="rp">
          <div>
            {reportView.offline ? (
              <PageError
                title="Sin conexión"
                description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
                onRetry={reportView.retry}
              />
            ) : (
              <PageError
                title="No pudimos cargar este reporte"
                description="Puede ser un problema de conexión. Intenta de nuevo."
                onRetry={reportView.retry}
              />
            )}
            <div className="rp-empty__actions" style={{ marginTop: 0 }}>
              <Link to="/tiendas" className="btn btn--ghost btn--sm">
                Volver a mis tiendas
              </Link>
            </div>
          </div>
        </main>
      </>
    );
  }

  const report = reportView.data.report;
  // A refetch that failed over a report already on screen keeps it, with
  // an inline notice — never a blank page over data the user can read.
  const refreshNotice = reportView.refreshFailed ? (
    <div className="notice-banner notice-banner--action">
      <span>No pudimos actualizar. Mostrando lo último que tienes guardado.</span>
      <button
        type="button"
        className="btn btn--secondary btn--sm"
        onClick={() => void reportQ.refetch()}
      >
        Reintentar
      </button>
    </div>
  ) : null;

  if (!terminal) {
    return (
      <>
        <RpTop assetId={report.assetId} storeName={storeName} />
        <main className="rp">
          {refreshNotice}
          <div className="empty">
            <div className="empty__title">Este reporte todavía está en curso</div>
            <div className="empty__sub">
              El reporte se completa cuando se cierra en terreno. Vuelve a intentarlo más tarde.
            </div>
            <div className="rp-empty__actions">
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => void reportQ.refetch()}
              >
                Reintentar
              </button>
              <Link to={`/tiendas/${report.assetId}`} className="btn btn--ghost btn--sm">
                Volver a la tienda
              </Link>
            </div>
          </div>
        </main>
      </>
    );
  }

  // --- Derived numbers (terminal report from here on) -------------------

  // reportTerminalAt() is non-null for every terminal report except a
  // `signed` row that lost both timestamps; createdAt is the honest
  // floor there rather than crashing the formatter.
  const closedIso = terminalAt ?? report.createdAt;
  const score = report.score ?? null;
  const onCount = score === null ? 0 : Math.max(0, Math.min(5, Math.round(score)));
  const segFull = score !== null && score >= 5;

  // "No bloques" and "bloques not loaded yet" are different states: a
  // report without a checklist (a residential / postventa acta reached
  // by URL, or a facility reportType with no bloques) must render an
  // explicit empty rather than «…» forever.
  // The section panels follow blocksView: a skeleton while the bloques
  // load (held through its minimum once painted), the panel's inline
  // retry on a first-load failure. `disabled` cannot happen on a terminal
  // report; if it did, nothing is on its way and it reads as no checklist.
  const blocksLoading = blocksView.kind === "loading";
  const blocksFailed = blocksView.kind === "error";
  const blocksSkelVisible = blocksView.kind === "loading" && blocksView.visible;
  const blocksKnown = !blocksLoading && !blocksFailed;
  const noChecklist = blocksKnown && blocks.length === 0;
  const reviewedTotal = blocks.reduce((n, b) => n + b.reviewedItems, 0);
  const allItemsLoaded = blocksKnown && blocks.every((b) => items.byArea[b.inspectionAreaId]);
  const factPlaceholder = blocksFailed ? "—" : "…";
  let cumpleTotal = 0;
  let naTotal = 0;
  let itemsTotal = 0;
  for (const b of blocks) {
    const data = items.byArea[b.inspectionAreaId];
    if (!data) continue;
    const c = countsFor(data.items);
    cumpleTotal += c.cumple;
    naTotal += c.na;
    itemsTotal += c.total;
  }

  const modCrit = observations.filter(
    (o) => o.severity === "moderada" || o.severity === "critica"
  ).length;

  // Delta vs. the previous closed visit of the same store. byAssetVisits
  // is ordered closedAt DESC and capped at 6 per asset server-side, so a
  // report older than the 6 most recent has no "previous" here even
  // when one exists in the DB. «Primer reporte registrado» is asserted
  // only when it is provable: the report is in the window, sits last,
  // and the window is not full. Every other no-predecessor case (report
  // outside the window, last slot of a full window, non-facility org,
  // aggregate failed) renders «—», never a false «Primera visita».
  const visitIdx = visits.findIndex((v) => v.reportId === id);
  const prevVisit = visitIdx >= 0 ? visits[visitIdx + 1] : undefined;
  const delta =
    prevVisit && prevVisit.score !== null && score !== null ? score - prevVisit.score : null;
  const provenFirstVisit =
    facilityEnabled &&
    !dashQ.error &&
    visitIdx >= 0 &&
    visitIdx === visits.length - 1 &&
    visits.length < VISITS_PER_ASSET_CAP;

  // «Estado hoy»: the only live column. Joined by finding id against
  // whichever live source the recipe declares (see the hooks above).
  // Unknown is rendered as unknown — an id missing from the aggregate's
  // recency window, a failed live read or a status this build does not
  // know all read «Estado no disponible», never «Sin asignar».
  function estadoHoy(o: FrozenObservation): Estado {
    if (liveLifecycle) {
      if (obsQ.isLoading) return ESTADO_PENDING;
      const live = liveById.get(o.id);
      if (!live) {
        return estadoUnavailable(
          obsQ.error
            ? "No pudimos cargar el estado actual"
            : "Sin estado vivo para esta observación"
        );
      }
      return {
        cls: observationStatusPillClass(live.observationStatus, observationStatuses),
        label: observationStatusLabel(live.observationStatus, observationStatuses),
      };
    }
    if (dashPending) return ESTADO_PENDING;
    if (!facilityEnabled) return estadoUnavailable("Sin seguimiento en vivo en esta organización");
    const entry = entryById.get(o.id);
    if (!entry) {
      return estadoUnavailable(
        dashQ.error ? "No pudimos cargar el tablero" : "Fuera de la ventana reciente del tablero"
      );
    }
    // ADR-0134: the thread's phase, painted with the recipe's own state
    // label («Abierta», «Asignada», «Resuelta», «Cerrada», «Anulada»). A
    // core-api that predates the thread fields sends no phase: unknown,
    // never a guess from the retired delegation column.
    if (entry.observationPhase === undefined)
      return estadoUnavailable("Estado desconocido para esta versión del tablero");
    const { cls, label } = entryEstadoPill(entry, recipe);
    return { cls, label };
  }

  const eyebrow = ["Reporte de tienda", storeCode, brandName, zoneName]
    .filter((s): s is string => !!s)
    .join(" · ");

  const sectionsError =
    blocksView.kind === "error" ? (
      <PanelError
        message="No pudimos cargar las secciones de este reporte."
        onRetry={blocksView.retry}
      />
    ) : null;
  const noChecklistEmpty = (
    <div className="empty">
      <div className="empty__title">Este reporte no usa checklist por secciones</div>
      <div className="empty__sub">Las observaciones levantadas siguen disponibles más abajo.</div>
    </div>
  );

  return (
    <>
      <RpTop assetId={report.assetId} storeName={storeName} fecha={shortDate} />
      <main className="rp">
        {refreshNotice}
        {/* 1. Header */}
        <div className="page-header" style={{ marginBottom: 0 }}>
          <div className="page-header__lead">
            <div className="page-header__eyebrow mono">{eyebrow}</div>
            <h1 className="page-header__title">{storeName}</h1>
            <div className="rp-meta">
              <span>
                {fmtLongDate(closedIso)} · {fmtTime(closedIso)}
              </span>
              {/* Supervisor segment intentionally omitted: reportDetailResponse
                  carries authorUserId only, never a display name. The
                  closer's name lives on GET /reports/:id/pdf-data.closedBy,
                  which this view deliberately does not call. */}
            </div>
          </div>
        </div>

        {/* 2. Seal */}
        <div className="rp-seal">
          <CheckIcon />
          <span>
            <strong>Reporte cerrado.</strong> Cerrado el {fmtShortDate(closedIso)} ·{" "}
            {fmtTime(closedIso)}; su contenido ya no cambia.
          </span>
          <span className="mono">Folio {report.folio ?? "—"}</span>
        </div>

        <div className="rp-grid">
          {/* 3. Estado general */}
          <div className="panel">
            <div className="panel__head">
              <div className="panel__title">Estado general de la tienda</div>
            </div>
            <div className="panel__body">
              <div className="rp-score">
                <span className="rp-score__n">{score === null ? "—" : score.toFixed(1)}</span>
                <span className="rp-score__of">/ 5</span>
              </div>
              <div className="rp-seg" aria-hidden="true">
                {Array.from({ length: 5 }, (_, i) => (
                  <span key={i} className={i < onCount ? (segFull ? "on full" : "on") : ""} />
                ))}
              </div>
              {blocksLoading ? (
                <PageSkeleton
                  label="Cargando los resultados…"
                  visible={blocksSkelVisible}
                  announce={false}
                  kind="section"
                >
                  <RpFactsSkeleton />
                </PageSkeleton>
              ) : (
                <div className="rp-facts">
                  <div>
                    <div className="rp-fact__k">Ítems revisados</div>
                    <div className="rp-fact__v">
                      {blocksFailed ? "—" : reviewedTotal}{" "}
                      <small>· {allItemsLoaded ? `${cumpleTotal} cumplen` : factPlaceholder}</small>
                    </div>
                  </div>
                  <div>
                    <div className="rp-fact__k">Observaciones</div>
                    <div className="rp-fact__v">
                      {observations.length} <small>· {modCrit} moderadas/críticas</small>
                    </div>
                  </div>
                  <div>
                    <div className="rp-fact__k">vs. reporte anterior</div>
                    <div className="rp-fact__v">
                      {dashPending ? (
                        "…"
                      ) : prevVisit !== undefined && delta !== null ? (
                        <>
                          {delta > 0 ? (
                            <span className="rp-delta rp-delta--up">
                              <span aria-hidden="true">▲ </span>
                              <span className="sr-only">Subió </span>+{delta.toFixed(2)}
                            </span>
                          ) : delta < 0 ? (
                            <span className="rp-delta rp-delta--down">
                              <span aria-hidden="true">▼ </span>
                              <span className="sr-only">Bajó </span>−{Math.abs(delta).toFixed(2)}
                            </span>
                          ) : (
                            <span className="rp-delta">Sin cambio</span>
                          )}{" "}
                          <small>
                            · {fmtDate(prevVisit.signedAt)}, {prevVisit.score?.toFixed(2)}
                          </small>
                        </>
                      ) : provenFirstVisit ? (
                        <span className="st-pill st-pill--idle">Primer reporte registrado</span>
                      ) : (
                        <span title="Sin datos del reporte anterior">—</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="rp-fact__k">No aplica</div>
                    <div className="rp-fact__v">
                      {allItemsLoaded ? naTotal : factPlaceholder}{" "}
                      <small>{naTotal === 1 ? "ítem" : "ítems"}</small>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 4. Resultado por sección */}
          <div className="panel">
            <div className="panel__head">
              <div className="panel__title">Resultado por sección</div>
              <div className="panel__sub mono">
                {blocksLoading
                  ? "…"
                  : blocksFailed
                    ? "—"
                    : `${blocks.length} ${blocks.length === 1 ? "sección" : "secciones"}`}
              </div>
            </div>
            <div className="panel__body" style={{ paddingTop: 6, paddingBottom: 6 }}>
              {sectionsError ? (
                sectionsError
              ) : blocksLoading ? (
                <PageSkeleton
                  label="Cargando las secciones…"
                  visible={blocksSkelVisible}
                  announce={false}
                  kind="section"
                >
                  <RpSecsSkeleton />
                </PageSkeleton>
              ) : noChecklist ? (
                noChecklistEmpty
              ) : (
                <div className="rp-secs">
                  {blocks.map((b, i) => {
                    const n = i + 1;
                    const data = items.byArea[b.inspectionAreaId];
                    const c = data ? countsFor(data.items) : null;
                    const pct = c ? compliancePct(c) : null;
                    return (
                      <a
                        key={b.inspectionAreaId}
                        className="rp-sec"
                        href={`#sec-${n}`}
                        onClick={() => setOpen(b.inspectionAreaId, true)}
                      >
                        <span className="rp-sec__idx">{pad2(n)}</span>
                        <span className="rp-sec__name">{b.label}</span>
                        <span className="rp-bar" aria-hidden="true">
                          {pct !== null && (
                            <i className={pct === 100 ? "" : "w"} style={{ width: `${pct}%` }} />
                          )}
                        </span>
                        <span className="rp-sec__cnt">
                          {!c
                            ? "…"
                            : pct === null
                              ? "Sin ítems aplicables"
                              : `${c.cumple}/${c.total} cumplen`}
                        </span>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 5. Observaciones levantadas */}
        <div className="panel">
          <div className="panel__head">
            <div className="panel__title">Observaciones levantadas</div>
            <div className="panel__sub mono">
              {observations.length} · Estado hoy: en vivo · el acta no cambia
            </div>
            {observations.length > 0 && (
              <div className="panel__actions">
                <Link
                  className="btn btn--ghost btn--sm"
                  to={`/observaciones?asset=${encodeURIComponent(report.assetId)}`}
                >
                  Ver todas las de esta tienda en la cola ↗
                </Link>
              </div>
            )}
          </div>
          <div className="panel__body--flush">
            {liveLifecycle && obsQ.error && observations.length > 0 && (
              <PanelError
                message="No pudimos cargar el estado actual de las observaciones."
                onRetry={() => void obsQ.refetch()}
              />
            )}
            {observations.length === 0 ? (
              <div className="empty">
                <div className="empty__title">Sin observaciones en este reporte</div>
                <div className="empty__sub">Todos los ítems evaluados cumplen o no aplican.</div>
              </div>
            ) : (
              <table className="dtable rp-obs">
                <thead>
                  <tr>
                    <th>Observación</th>
                    <th>Sección</th>
                    <th>Fotos</th>
                    <th>Severidad</th>
                    <th>Estado hoy</th>
                  </tr>
                </thead>
                <tbody>
                  {observations.map((o) => (
                    <ObservationRow key={o.id} o={o} estado={estadoHoy(o)} />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* 6. Detalle por sección */}
        <div className="panel">
          <div className="panel__head">
            <div className="panel__title">Detalle por sección</div>
            <div className="panel__sub mono">
              {allItemsLoaded
                ? `${itemsTotal} ${itemsTotal === 1 ? "ítem" : "ítems"}`
                : factPlaceholder}
            </div>
            {blocks.length > 0 && (
              <div className="panel__actions">
                <button type="button" className="btn btn--ghost btn--sm" onClick={toggleAll}>
                  {allOpen ? "Contraer todo" : "Expandir todo"}
                </button>
              </div>
            )}
          </div>
          <div>
            {sectionsError ? (
              sectionsError
            ) : blocksLoading ? (
              <PageSkeleton
                label="Cargando las secciones…"
                visible={blocksSkelVisible}
                announce={false}
                kind="section"
              >
                <RpDetSkeleton />
              </PageSkeleton>
            ) : noChecklist ? (
              noChecklistEmpty
            ) : (
              blocks.map((b, i) => (
                <SectionDetails
                  key={b.inspectionAreaId}
                  n={i + 1}
                  block={b}
                  data={items.byArea[b.inspectionAreaId]}
                  error={items.errors[b.inspectionAreaId]}
                  onRetry={items.refetch[b.inspectionAreaId]}
                  open={openByArea[b.inspectionAreaId] ?? false}
                  onToggle={(open) => setOpen(b.inspectionAreaId, open)}
                  obsByAreaItem={obsByAreaItem}
                />
              ))
            )}
          </div>
        </div>

        {/* 7. Foot note */}
        <p className="rp-foot">
          Este reporte es inmutable: refleja lo verificado al momento del cierre. «Estado hoy» es lo
          único que se actualiza.
        </p>
      </main>
    </>
  );
}

// Every link from the acta into an observación carries the acta's own URL
// as the back origin, so the detail's "Volver" lands here instead of on
// the full observaciones list (lib/backOrigin.ts).
function useActaBackState() {
  const { pathname } = useLocation();
  return backOriginState(pathname, "Volver al acta");
}

function ObservationRow({ o, estado }: { o: FrozenObservation; estado: Estado }) {
  const navigate = useNavigate();
  const backState = useActaBackState();
  const headline = observationHeadline(o);
  const sub = [o.areaName, o.element].filter((s): s is string => !!s).join(" · ");
  // Same row-link contract as Observaciones / DetalleTienda: the title
  // is a real <Link> (keyboard + AT path, SC 2.1.1); the row onClick is
  // mouse-only sugar that yields to any interactive child.
  function handleRowClick(e: React.MouseEvent<HTMLTableRowElement>) {
    if (
      (e.target as HTMLElement).closest(
        'a, button, [role="button"], [role="combobox"], [role="listbox"], [role="option"], [role="dialog"]'
      )
    )
      return;
    navigate(`/observaciones/${o.id}`, { state: backState });
  }
  return (
    <tr className="row-link" onClick={handleRowClick}>
      <td>
        <div className="cell-with-sub cell-with-sub--headline">
          <Link
            to={`/observaciones/${o.id}`}
            state={backState}
            className="cell-strong cell-strong--link"
          >
            {headline.text}
            {headline.fullText && headline.fullText !== headline.text && (
              <span className="sr-only"> — {headline.fullText}</span>
            )}
          </Link>
          <span className="cell-sub">{sub}</span>
        </div>
      </td>
      <td>{o.areaName}</td>
      <td>
        {/* Shared cell: broken-image fallback + «N fotos» overflow count,
            so a deleted R2 object never leaks a broken glyph into the
            sealed acta and the manager always sees the real photo count. */}
        <EvidenciaCell count={o.photoUrls.length} thumbUrls={o.photoUrls} />
      </td>
      <td>
        <span className={`st-pill ${sevPillClass(o.severity)}`}>{sevLabel(o.severity)}</span>
      </td>
      <td>
        <span className={`st-pill ${estado.cls}`} title={estado.title}>
          {estado.label}
        </span>
      </td>
    </tr>
  );
}

function SectionDetails({
  n,
  block,
  data,
  error,
  onRetry,
  open,
  onToggle,
  obsByAreaItem,
}: {
  n: number;
  block: BlockState;
  data: { items: BlockItemState[] } | undefined;
  error: unknown;
  onRetry: (() => void) | undefined;
  open: boolean;
  onToggle: (open: boolean) => void;
  obsByAreaItem: ReadonlyMap<string, FrozenObservation[]>;
}) {
  const c = data ? countsFor(data.items) : null;
  const sorted = data ? data.items.slice().sort((a, b) => a.displayOrder - b.displayOrder) : [];
  const backState = useActaBackState();
  // One query per bloque, folded by useReportBlockItems into maps, so the
  // phase machine runs here on "no items and no error yet". A painted
  // skeleton holds through its minimum even if the items already landed.
  const itemsLoading = !data && error === undefined;
  const phase = useSkeletonPhase(itemsLoading);
  const skelVisible = isSkeletonVisible(phase);
  // The bloque declares its item count: the skeleton draws exactly that.
  const skelRows = Math.min(Math.max(block.totalItems, 1), 12);
  return (
    <details
      className="rp-det"
      id={`sec-${n}`}
      tabIndex={-1}
      open={open}
      onToggle={(e) => onToggle(e.currentTarget.open)}
    >
      <summary>
        <span className="rp-sec__idx">{pad2(n)}</span>
        <span className="rp-det__title">{block.label}</span>
        <span className="rp-sec__cnt">{c ? `${c.cumple}/${c.total} cumplen` : "…"}</span>
        <span className="rp-det__chev">
          <ChevronIcon />
        </span>
      </summary>
      {error !== undefined ? (
        <div className="rp-det__error" role="alert">
          <span>No pudimos cargar el detalle de esta sección.</span>
          {onRetry && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={onRetry}>
              Reintentar
            </button>
          )}
        </div>
      ) : itemsLoading || skelVisible || !data ? (
        <PageSkeleton
          label="Cargando los ítems…"
          visible={skelVisible}
          announce={false}
          kind="section"
        >
          <RpItemsSkeleton rows={skelRows} />
        </PageSkeleton>
      ) : (
        <ul className="rp-items">
          {sorted.map((it, i) => {
            const v = effectiveVerdict(it);
            const linked = needsAttention(v)
              ? (obsByAreaItem.get(matchKey(block.label, it.label)) ?? [])
              : [];
            return (
              <li key={it.itemId} className="rp-item">
                <span className="rp-item__n">
                  {n}.{i + 1}
                </span>
                <span>
                  {it.label}
                  {linked.map((o) => (
                    <span key={o.id} className="rp-item__obs">
                      ↳{" "}
                      <Link to={`/observaciones/${o.id}`} state={backState}>
                        {observationHeadline(o).text}
                      </Link>
                    </span>
                  ))}
                </span>
                <VerdictPill verdict={v} />
              </li>
            );
          })}
        </ul>
      )}
    </details>
  );
}
